import { Dispatch, SetStateAction, useContext } from 'react';
import {
  DevHubBox,
  DevHubButton,
  DevHubLink,
  DevHubModal,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { ApolloError, useMutation } from '@apollo/client';
import { DocumentNode } from 'graphql';
import { Submitted } from '../roles/Submitted';
import { DeleteCloudAccountMutation } from './CloudAccountQueries';
import { SystemContext } from '../../../contexts/SystemContext';

type Props = {
  appName: string | undefined;
  cloudAccountName: string;
  handleOpen: React.MouseEventHandler<HTMLButtonElement>;
  handleClose: React.MouseEventHandler<HTMLButtonElement>;
  applicationId: number;
  cloudAccountId: string;
  cloudAccountsQuery: DocumentNode;
  submitted: boolean;
  setSubmitted: Dispatch<SetStateAction<boolean>>;
  open: boolean;
};

/**
 * This component is used to remove an association between a cloud account and an application.
 * @param props - The props for the component.
 */
export function RemoveAssociation(props: Props) {
  const { GetApplicationQuery, applicationVariables } =
    useContext(SystemContext);

  const [deleteAssociation, { data, loading, error }] = useMutation(
    DeleteCloudAccountMutation,
    {
      refetchQueries: [
        {
          query: props.cloudAccountsQuery,
          variables: { applicationId: props.applicationId },
        },
        {
          query: GetApplicationQuery,
          variables: applicationVariables,
        },
        'GetApplicationRoleAndCloudAccountsDetails',
      ],
    },
  );

  const handleSubmit = () => {
    const deleteInput = {
      applicationId: props.applicationId,
      cloudAccountId: props.cloudAccountId,
    };
    deleteAssociation({ variables: { input: deleteInput } });
    props.setSubmitted(true);
  };

  const getTitle = () => {
    return `Please confirm the removal of the association between cloud account (${props?.cloudAccountName}) and ${props?.appName}?`;
  };

  const getErrorMessage = (
    mutationError?: ApolloError,
  ): string | React.ReactElement => {
    if (
      mutationError?.graphQLErrors?.find(
        x => x.extensions?.code === 'LAST_APPLICATION_CONNECTION',
      )
    ) {
      return (
        <DevHubTypography>
          A cloud account must be linked to at least one active application.
          Kindly reassign this cloud account to a different application to
          discontinue this association. If your intention is to decommission the
          account instead, please initiate the decommissioning process by
          following{' '}
          <DevHubLink
            target="_blank"
            href="https://docs-dx.comcast.com/kb/data-management/additional-resources-data-management/data-decommission-guidelines/"
          >
            this link.
          </DevHubLink>
        </DevHubTypography>
      );
    }

    return 'Error deleting association.';
  };

  return (
    <DevHubModal open={props?.open} onClose={props?.handleClose}>
      {props.submitted ? (
        <Submitted
          error={error}
          submitted={props?.submitted}
          loading={loading}
          handleClose={props?.handleClose}
          success={
            data?.devhub?.connections?.cloudAccount?.removeApplicationConnection
              ?.success
          }
          errorMessage={getErrorMessage(error)}
        />
      ) : (
        <>
          <DevHubTypography paragraph>{getTitle()}</DevHubTypography>
          <DevHubBox
            display="flex"
            justifyContent="space-around"
            alignItems="center"
          >
            <DevHubButton
              height="medium"
              variant="contained"
              color="primary"
              onClick={props?.handleClose}
            >
              Cancel
            </DevHubButton>
            <DevHubButton
              height="medium"
              variant="contained"
              color="primary"
              onClick={handleSubmit}
            >
              Confirm
            </DevHubButton>
          </DevHubBox>
        </>
      )}
    </DevHubModal>
  );
}
